import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Building2, 
  Users, 
  Stethoscope,
  Home,
  Pill,
  Truck,
  Shield,
  Info,
  CheckCircle,
  AlertCircle,
  Edit3,
  Save,
  X,
  Settings,
  DollarSign,
  BarChart3
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';

const CompanyCOTManagement = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCOT, setSelectedCOT] = useState(null);
  const [showImpactModal, setShowImpactModal] = useState(false);

  // COT definitions with features and pricing impact
  const COT_DEFINITIONS = {
    RETAIL_PHARMACY: {
      name: 'Retail Pharmacy',
      icon: Pill,
      description: 'Independent and chain retail pharmacies serving individual consumers',
      features: [
        'Individual patient prescriptions',
        'Over-the-counter sales',
        'Insurance billing',
        'Patient counseling services',
        'Basic reporting tools'
      ],
      pricingTier: 'Standard',
      monthlyFee: '$299',
      transactionFee: '$0.15 per transaction',
      maxUsers: 5,
      maxCustomers: 100,
      maxProducts: 1000
    },
    HOSPITAL: {
      name: 'Hospital',
      icon: Stethoscope,
      description: 'Hospitals and medical centers with inpatient and outpatient services',
      features: [
        'Inpatient and outpatient dispensing',
        'Bulk medication management',
        'Advanced inventory tracking',
        'Clinical decision support',
        'Comprehensive reporting',
        'Integration with hospital systems'
      ],
      pricingTier: 'Professional',
      monthlyFee: '$799',
      transactionFee: '$0.10 per transaction',
      maxUsers: 25,
      maxCustomers: 500,
      maxProducts: 5000
    },
    LONG_TERM_CARE: {
      name: 'Long-term Care Facility',
      icon: Home,
      description: 'Nursing homes, assisted living, and long-term care facilities',
      features: [
        'Unit-dose packaging',
        'Medication administration records',
        'Resident-specific dispensing',
        'Compliance tracking',
        'Family communication tools',
        'Regulatory reporting'
      ],
      pricingTier: 'Professional',
      monthlyFee: '$599',
      transactionFee: '$0.12 per transaction',
      maxUsers: 15,
      maxCustomers: 200,
      maxProducts: 2000
    },
    SPECIALTY_PHARMACY: {
      name: 'Specialty Pharmacy',
      icon: Pill,
      description: 'Specialized pharmacies focusing on complex, high-cost medications',
      features: [
        'Specialty medication management',
        'Prior authorization support',
        'Patient assistance programs',
        'Clinical monitoring',
        'Advanced analytics',
        'Manufacturer partnerships'
      ],
      pricingTier: 'Enterprise',
      monthlyFee: '$1,299',
      transactionFee: '$0.08 per transaction',
      maxUsers: 50,
      maxCustomers: 1000,
      maxProducts: 10000
    },
    WHOLESALER: {
      name: 'Wholesaler/Distributor',
      icon: Truck,
      description: 'Pharmaceutical wholesalers and distributors',
      features: [
        'Bulk distribution management',
        'Supply chain optimization',
        'Multi-location support',
        'Advanced inventory management',
        'Compliance tracking',
        'API integrations'
      ],
      pricingTier: 'Enterprise',
      monthlyFee: '$1,999',
      transactionFee: '$0.05 per transaction',
      maxUsers: 100,
      maxCustomers: 5000,
      maxProducts: 50000
    },
    GOVERNMENT_FACILITY: {
      name: 'Government Facility',
      icon: Shield,
      description: 'VA hospitals, military facilities, and government healthcare',
      features: [
        'Government compliance',
        'Secure data handling',
        'Audit trail management',
        'Regulatory reporting',
        'Multi-facility coordination',
        'Custom integrations'
      ],
      pricingTier: 'Government',
      monthlyFee: 'Custom',
      transactionFee: 'Custom',
      maxUsers: 'Unlimited',
      maxCustomers: 'Unlimited',
      maxProducts: 'Unlimited'
    }
  };

  // Fetch current company COT
  const { data: companyCOT, isLoading } = useQuery({
    queryKey: ['companyCOT'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/company/cot`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      return data.data;
    }
  });

  // Update company COT mutation
  const updateCOTMutation = useMutation({
    mutationFn: async (newCOT) => {
      const response = await fetch(`${API_BASE_URL}/api/company/cot`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ classOfTrade: newCOT })
      });
      const data = await response.json();
      return data;
    },
    onSuccess: () => {
      toast.success('Company Class of Trade updated successfully');
      queryClient.invalidateQueries({ queryKey: ['companyCOT'] });
      setIsEditing(false);
      setSelectedCOT(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update Class of Trade');
    }
  });

  const handleCOTChange = (cotCode) => {
    setSelectedCOT(cotCode);
    setShowImpactModal(true);
  };

  const confirmCOTChange = () => {
    if (selectedCOT) {
      updateCOTMutation.mutate(selectedCOT);
    }
    setShowImpactModal(false);
  };

  const currentCOT = companyCOT?.classOfTrade || 'RETAIL_PHARMACY';
  const currentCOTInfo = COT_DEFINITIONS[currentCOT];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Company Class of Trade Management</h1>
        <p className="text-gray-600 mt-2">
          Manage your organization's Class of Trade classification. This affects system features, pricing tiers, and available functionality.
        </p>
      </div>

      {/* Current Classification */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Current Classification</h2>
              <p className="text-sm text-gray-500 mt-1">Your organization's current Class of Trade</p>
            </div>
            <button
              onClick={() => setIsEditing(!isEditing)}
              title={isEditing ? 'Cancel' : 'Change Class of Trade'}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2"
            >
              <Edit3 className="h-4 w-4" />
              <span>{isEditing ? 'Cancel' : 'Change COT'}</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <currentCOTInfo.icon className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900">{currentCOTInfo.name}</h3>
              <p className="text-gray-600 mt-1">{currentCOTInfo.description}</p>
              
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-500">Pricing Tier</div>
                  <div className="font-semibold text-gray-900">{currentCOTInfo.pricingTier}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-500">Monthly Fee</div>
                  <div className="font-semibold text-gray-900">{currentCOTInfo.monthlyFee}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-500">Transaction Fee</div>
                  <div className="font-semibold text-gray-900">{currentCOTInfo.transactionFee}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* COT Selection */}
      {isEditing && (
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Select New Class of Trade</h2>
            <p className="text-sm text-gray-500 mt-1">Choose the classification that best represents your organization</p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(COT_DEFINITIONS).map(([code, info]) => (
                <div
                  key={code}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedCOT === code
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedCOT(code)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedCOT === code ? 'bg-primary-100' : 'bg-gray-100'
                      }`}>
                        <info.icon className={`h-5 w-5 ${
                          selectedCOT === code ? 'text-primary-600' : 'text-gray-600'
                        }`} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">{info.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{info.description}</p>
                      <div className="mt-2 text-xs text-gray-500">
                        {info.pricingTier} • {info.monthlyFee}/month
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {selectedCOT && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Selected: {COT_DEFINITIONS[selectedCOT].name}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-medium text-gray-700">Features:</div>
                    <ul className="mt-1 space-y-1">
                      {COT_DEFINITIONS[selectedCOT].features.slice(0, 3).map((feature, index) => (
                        <li key={index} className="text-gray-600">• {feature}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Limits:</div>
                    <div className="mt-1 space-y-1 text-gray-600">
                      <div>Users: {COT_DEFINITIONS[selectedCOT].maxUsers}</div>
                      <div>Customers: {COT_DEFINITIONS[selectedCOT].maxCustomers}</div>
                      <div>Products: {COT_DEFINITIONS[selectedCOT].maxProducts}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setSelectedCOT(null);
                }}
                title="Cancel"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleCOTChange(selectedCOT)}
                title="Update COT"
                disabled={!selectedCOT || updateCOTMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center space-x-2"
              >
                <Save className="h-4 w-4" />
                <span>Update COT</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Impact Modal */}
      {showImpactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirm COT Change</h3>
                  <p className="text-sm text-gray-500">This will affect your entire system</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  Changing your Class of Trade to <strong>{COT_DEFINITIONS[selectedCOT]?.name}</strong> will:
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Update your pricing tier and fees</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Modify available features and functionality</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Adjust user and data limits</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <AlertCircle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Require system restart to apply changes</span>
                  </li>
                </ul>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowImpactModal(false)}
                  title="Cancel"
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmCOTChange}
                  title="Confirm Change"
                  disabled={updateCOTMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {updateCOTMutation.isPending ? 'Updating...' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyCOTManagement;
